import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import { RequestService } from "services/RequestService";

function AddForm({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: "",
    powerStationId: "",
  });

  const [powerStations, setPowerStations] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    open: false,
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const fetchPowerStations = async () => {
    try {
      const response = await RequestService.get("PowerStation");
      if (response.data && !response.data.error) {
        setPowerStations(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل بيانات المحطات",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching power stations:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل بيانات المحطات",
        open: true
      });
    }
  };

  useEffect(() => {
    if (open) {
      fetchPowerStations();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "اسم التوربين مطلوب";
    }
    if (!formData.powerStationId) {
      newErrors.powerStationId = "المحطة مطلوبة";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await RequestService.post("ST", {
        name: formData.name,
        powerStationId: Number(formData.powerStationId),
      });

      if (response.data && !response.data.error) {
        onSuccess(response.data?.message);
        handleClose();
      } else {
        setErrors({
          submit: response.data?.message || "فشل في إضافة التوربين البخاري"
        });
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في إضافة التوربين البخاري",
          open: true
        });
      }
    } catch (error) {
      console.error("Error adding steam turbine:", error);
      setErrors({
        submit: error.response?.data?.message || "حدث خطأ أثناء إضافة التوربين البخاري"
      });
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء إضافة التوربين البخاري",
        open: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      powerStationId: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          <MDTypography variant="h6">إضافة توربين بخاري جديد</MDTypography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <MDBox pt={2} pb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDInput
                    fullWidth
                    name="name"
                    label="اسم التوربين"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.powerStationId}>
                    <InputLabel>المحطة</InputLabel>
                    <Select
                      value={formData.powerStationId}
                      name="powerStationId"
                      label="المحطة"
                      onChange={handleChange}
                      sx={{ 
                        height: "45px",
                        "& .MuiSelect-select": {
                          paddingTop: "10px",
                          paddingBottom: "10px"
                        }
                      }}
                    >
                      {powerStations.map((station) => (
                        <MenuItem key={station.id} value={station.id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.powerStationId && (
                      <MDTypography variant="caption" color="error">
                        {errors.powerStationId}
                      </MDTypography>
                    )}
                  </FormControl>
                </Grid>
                {errors.submit && (
                  <Grid item xs={12}>
                    <MDTypography color="error" variant="caption">
                      {errors.submit}
                    </MDTypography>
                  </Grid>
                )}
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ 
            padding: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            '& > button': {
              marginLeft: '8px',
              marginRight: '0px',
            }
          }}>
            <MDButton type="submit" color="info" disabled={isSubmitting}>
              {isSubmitting ? "جاري الإضافة..." : "إضافة"}
            </MDButton>
            <MDButton onClick={handleClose} color="secondary">
              إلغاء
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </>
  );
}

AddForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddForm;
